import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { X, ExternalLink, Download, Loader2, ChevronDown, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { useJob, useJobLogs, useJobAssignments, useJobCallbackLogs, useJobProviderAttempts } from '@/hooks/useJobs';
import InfoItem from './InfoItem';
import JobStatusBadge from './JobStatusBadge';
import LogViewer from './LogViewer';
import ImageCard from './ImageCard';
import ImageLightbox, { type LightboxImage } from './ImageLightbox';
import ZipImageViewer from './ZipImageViewer';
import CallbackUrlInfo from './CallbackUrlInfo';
import CallbackLogsSection from './CallbackLogsSection';
import ProviderAttemptsToggle from './ProviderAttemptsToggle';
import ProviderAttemptsSection from './ProviderAttemptsSection';
import { getFilenameFromUrl, extractJobImages } from '@/lib/imageUtils';
import { downloadJobData, type DownloadProgress } from '@/lib/downloadJobData';
import { hasZipExtension } from '@/lib/imageUtils';
import { formatDuration } from '@/lib/utils';

interface JobDetailsModalProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
  jobIds?: string[];
  onNavigate?: (jobId: string) => void;
}

export default function JobDetailsModal({ jobId, isOpen, onClose, jobIds, onNavigate }: JobDetailsModalProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showInputImage, setShowInputImage] = useState(false);
  const [showOutputImages, setShowOutputImages] = useState(false);
  const [inputImageUrl, setInputImageUrl] = useState<string | null>(null);
  const [outputImageUrls, setOutputImageUrls] = useState<string[]>([]);
  const [lightboxState, setLightboxState] = useState<{ images: LightboxImage[]; index: number } | null>(null);
  const outputLightboxImages = useMemo<LightboxImage[]>(
    () => outputImageUrls.map(u => ({ url: u, filename: getFilenameFromUrl(u) })),
    [outputImageUrls]
  );
  const [showCallbackLogs, setShowCallbackLogs] = useState(false);
  const [showProviderAttempts, setShowProviderAttempts] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: job, isLoading: jobLoading } = useJob(jobId);
  const { data: logsData } = useJobLogs(jobId);
  const { data: assignmentsData } = useJobAssignments(jobId);
  const { data: callbackLogs, isLoading: callbackLogsLoading } = useJobCallbackLogs(jobId, showCallbackLogs);
  const { data: providerAttempts, isLoading: providerAttemptsLoading } = useJobProviderAttempts(
    jobId,
    showProviderAttempts && job?.type_name === 'model-run',
  );

  // Navigation state
  const currentIndex = jobIds?.indexOf(jobId) ?? -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (jobIds?.length ?? 0) - 1;

  const goToPrev = useCallback(() => {
    if (hasPrev && jobIds && onNavigate) onNavigate(jobIds[currentIndex - 1]);
  }, [hasPrev, jobIds, onNavigate, currentIndex]);

  const goToNext = useCallback(() => {
    if (hasNext && jobIds && onNavigate) onNavigate(jobIds[currentIndex + 1]);
  }, [hasNext, jobIds, onNavigate, currentIndex]);

  // Detect images when job loads (uses GET-abort for extensionless URLs). The parent mounts this
  // modal keyed by jobId, so switching jobs remounts it and every per-job piece of state — the
  // disclosure toggles, the lightbox, and these image URLs — resets for free. The cancelled guard
  // covers a same-job refetch: an earlier, slower extraction must not overwrite the images derived
  // from a newer job object (extractJobImages can wait seconds on extensionless URLs).
  useEffect(() => {
    if (!job) return;
    let cancelled = false;
    extractJobImages(job).then((images) => {
      if (cancelled) return;
      setInputImageUrl(images.input || null);
      setOutputImageUrls(images.output);
    });
    return () => {
      cancelled = true;
    };
  }, [job]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if lightbox is open — it handles its own keyboard events
      if (lightboxState) return;

      // Skip navigation if user is typing in an input
      const activeElement = document.activeElement;
      const isTyping = activeElement instanceof HTMLElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.isContentEditable
      );

      if (event.key === 'Escape') {
        onClose();
      } else if (!isTyping) {
        if (event.key === 'ArrowLeft' && hasPrev) {
          event.preventDefault();
          goToPrev();
        } else if (event.key === 'ArrowRight' && hasNext) {
          event.preventDefault();
          goToNext();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, lightboxState, onClose, hasPrev, hasNext, goToPrev, goToNext]);

  if (!isOpen) return null;

  const assignment = assignmentsData?.items?.[0];
  const logs = logsData?.items || [];

  const handleDownload = async (includeFiles: boolean) => {
    if (!job) return;

    setIsDropdownOpen(false);
    setIsDownloading(true);
    setDownloadProgress(null);
    try {
      await downloadJobData(job, logs, assignmentsData?.items || [], includeFiles, (progress) => {
        setDownloadProgress(progress);
      });
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Failed to download job data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-6xl max-h-[90vh] m-4 glass-card rounded-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold">Job Details</h2>
            {jobIds && jobIds.length > 1 && currentIndex >= 0 && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <button
                  type="button"
                  onClick={goToPrev}
                  disabled={!hasPrev}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Previous job (Left arrow)"
                  aria-label="Previous job"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span>{currentIndex + 1} of {jobIds.length}</span>
                <button
                  type="button"
                  onClick={goToNext}
                  disabled={!hasNext}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Next job (Right arrow)"
                  aria-label="Next job"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
            {job && <JobStatusBadge status={job.status_name} />}
          </div>
          <div className="flex items-center gap-2">
            {job && (
              <div ref={dropdownRef} className="relative">
                <button
                  type="button"
                  onClick={() => !isDownloading && setIsDropdownOpen(!isDropdownOpen)}
                  disabled={isDownloading}
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download job data as ZIP"
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span>
                    {isDownloading && downloadProgress ? (
                      <>
                        {downloadProgress.phase === 'preparing' && 'Preparing...'}
                        {downloadProgress.phase === 'downloading' && (
                          <>
                            {downloadProgress.filesDownloaded}/{downloadProgress.totalFiles}
                            {downloadProgress.currentFile && `: ${downloadProgress.currentFile}`}
                          </>
                        )}
                        {downloadProgress.phase === 'packaging' && 'Packaging...'}
                      </>
                    ) : isDownloading ? (
                      'Downloading...'
                    ) : (
                      'Download'
                    )}
                  </span>
                  {!isDownloading && <ChevronDown className="h-4 w-4" />}
                </button>

                {isDropdownOpen && !isDownloading && (
                  <div className="absolute right-0 mt-2 w-64 bg-background rounded-lg border border-border shadow-lg overflow-hidden z-10">
                    <button
                      type="button"
                      onClick={() => handleDownload(false)}
                      className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border"
                    >
                      <div className="font-medium text-sm">Download Metadata Only</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        JSON files and logs (fast, small)
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload(true)}
                      className="w-full px-4 py-3 text-left hover:bg-muted transition-colors"
                    >
                      <div className="font-medium text-sm flex items-center gap-2">
                        Download All Files
                        <span className="text-yellow-500">⚠️</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Includes input/output files (potentially large)
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
            <Link
              to={`/jobs/${jobId}`}
              className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors text-sm font-medium"
            >
              <span>Open detailed view</span>
              <ExternalLink className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {jobLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">Loading job details...</div>
            </div>
          ) : job ? (
            <>
              {/* Job Overview */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem label="Job ID" value={job.id} monospace />
                  <InfoItem label="Type" value={job.type_name} />
                  <InfoItem label="Priority" value={job.priority.toString()} />
                  <InfoItem label="Created" value={job.create_ts} monospace />
                  <InfoItem label="Started" value={job.start_ts || "—"} monospace />
                  <InfoItem label="Completed" value={job.complete_ts || "—"} monospace />
                  <InfoItem label="Duration" value={job.start_ts ? formatDuration(job.start_ts, job.complete_ts) : "—"} />
                  <InfoItem label="Attempt" value={job.attempt_number.toString()} />
                  <InfoItem label="Retries" value={`${job.retry_count} / ${job.max_retries}`} />
                  <InfoItem label="Callback URL" monospace>
                    {job.callback_url ? (
                      <CallbackUrlInfo
                        callbackUrl={job.callback_url}
                        showCallbackLogs={showCallbackLogs}
                        onToggleLogs={() => setShowCallbackLogs(!showCallbackLogs)}
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </InfoItem>

                  {/* Debug toggle — only for jobs that proxy to external providers (model-run). */}
                  {job.type_name === 'model-run' && (
                    <div className="md:col-span-2 flex flex-wrap gap-2 pt-2">
                      <ProviderAttemptsToggle
                        show={showProviderAttempts}
                        onToggle={() => setShowProviderAttempts(!showProviderAttempts)}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Callback Delivery Logs — own section when toggled on */}
              {showCallbackLogs && job.callback_url && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Callback Delivery Logs</h3>
                  <CallbackLogsSection callbackLogs={callbackLogs} isLoading={callbackLogsLoading} />
                </div>
              )}

              {/* Provider Attempts — own section when toggled on */}
              {showProviderAttempts && job.type_name === 'model-run' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Provider Attempts</h3>
                  <ProviderAttemptsSection attempts={providerAttempts} isLoading={providerAttemptsLoading} />
                </div>
              )}

              {/* Worker Assignment */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Worker Assignment</h3>
                {assignment ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem label="Worker ID" value={assignment.worker_id} monospace />
                    <InfoItem label="Claimed" value={assignment.create_ts || "—"} monospace />
                    <InfoItem label="Last Active" value={assignment.heartbeat_ts || "—"} monospace />
                    <InfoItem label="Released" value={assignment.release_ts || "—"} monospace />
                  </div>
                ) : (
                  <div className="text-muted-foreground text-sm">No worker assigned yet</div>
                )}
              </div>

              {/* Payload */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Payload</h3>
                <div className="bg-muted/50 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm font-mono text-muted-foreground">
                    {JSON.stringify(job.payload, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Input Images - ZIP */}
              {job.payload?.input_file && typeof job.payload.input_file === 'string' && hasZipExtension(job.payload.input_file) && (
                <ZipImageViewer
                  zipUrl={job.payload.input_file}
                  title="Input Images (from ZIP)"
                  compact={true}
                />
              )}

              {/* Input Image - Regular */}
              {inputImageUrl && !hasZipExtension(job.payload?.input_file || '') && (
                <div className="glass-card rounded-lg p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Input Image</h3>
                    <button
                      type="button"
                      onClick={() => setShowInputImage(!showInputImage)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors text-sm font-medium"
                    >
                      <Eye className="h-4 w-4" />
                      {showInputImage ? 'Hide' : 'View'} images
                    </button>
                  </div>
                  {showInputImage && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <ImageCard
                        url={inputImageUrl}
                        compact={true}
                        naturalLoading={true}
                        onOpenLightbox={() => setLightboxState({ images: [{ url: inputImageUrl, filename: getFilenameFromUrl(inputImageUrl) }], index: 0 })}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Result (if completed) */}
              {job.result && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Result</h3>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-sm font-mono text-green-400">
                      {JSON.stringify(job.result, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Output Images */}
              {outputImageUrls.length > 0 && (
                <div className="glass-card rounded-lg p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Output Images ({outputImageUrls.length})</h3>
                    <button
                      type="button"
                      onClick={() => setShowOutputImages(!showOutputImages)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors text-sm font-medium"
                    >
                      <Eye className="h-4 w-4" />
                      {showOutputImages ? 'Hide' : 'View'} images
                    </button>
                  </div>
                  {showOutputImages && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {outputImageUrls.map((url, i) => (
                        <ImageCard
                          key={url}
                          url={url}
                          compact={true}
                          naturalLoading={true}
                          onOpenLightbox={() => setLightboxState({
                            images: outputLightboxImages,
                            index: i,
                          })}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Error (if failed) */}
              {job.error && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Error</h3>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-sm font-mono text-red-400">
                      {JSON.stringify(job.error, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Logs */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Logs</h3>
                <LogViewer logs={logs} autoScroll={false} />
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">Job not found</div>
            </div>
          )}
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxState && (
        <ImageLightbox
          images={lightboxState.images}
          currentIndex={lightboxState.index}
          isOpen={!!lightboxState}
          onClose={() => setLightboxState(null)}
          onNavigate={(index) => setLightboxState(prev => prev ? { ...prev, index } : null)}
        />
      )}
    </div>
  );
}
